import { MatchEntityRegistry } from './matchEntityRegistry.js';
import { resolveMatchOutcome } from '../domain/matchOutcome.js';
import { TT_ALTAR_CONFIG } from '../domain/map/ttAltarRules.js';
import { expireWards, placeWard as createWard } from '../domain/vision.js';

const OPEN_OUTCOME = Object.freeze({
  ended: false,
  winnerTeam: null,
  loserTeam: null,
  destroyedNexusId: null
});

/**
 * MatchRuntime — Owns match-scoped entity registry + clock + last map plan.
 * P3-01 entity SoT + match clock; P3-02 mapId load via applyMapLoad; P3-05 nexus outcome.
 *
 * Contrato:
 *   seedSandbox / applyMapLoad / advanceTime / setGameTime / unlockAltars /
 *   syncOutcome / toOrchestratorEntityFields / getDamageableTargets
 */
export class MatchRuntime {
  constructor({ mapId = 'test_map' } = {}) {
    this.mapId = mapId;
    this.gameTime = 0;
    this.lastPlan = null;
    this.entities = new MatchEntityRegistry();
    this.outcome = { ...OPEN_OUTCOME };
    this.activeWards = [];
    this.visionAcc = 0;
  }

  advanceTime(dt = 0) {
    this.gameTime += dt;
    this.activeWards = expireWards(this.activeWards, this.gameTime);
  }

  setGameTime(seconds) {
    const next = Number(seconds);
    this.gameTime = Number.isFinite(next) ? Math.max(0, next) : 0;
    this.activeWards = expireWards(this.activeWards, this.gameTime);
    return this.gameTime;
  }

  unlockAltars() {
    return this.setGameTime(Math.max(this.gameTime, TT_ALTAR_CONFIG.unlockTime));
  }

  applyMapLoad(plan) {
    if (!plan || !plan.mapId) {
      throw new Error('MatchRuntime.applyMapLoad requires plan.mapId');
    }
    this.mapId = plan.mapId;
    this.gameTime = 0;
    this.lastPlan = plan;
    this.outcome = { ...OPEN_OUTCOME };
    this.activeWards = [];
    this.visionAcc = 0;
  }

  /**
   * Place a ward at sandbox xz (P3-10). Deducts nothing — caller handles shop/gold.
   */
  placeWard(type, x, z, team = 'blue') {
    const ward = createWard(type, x, z, team, this.gameTime);
    this.activeWards.push(ward);
    return ward;
  }

  /**
   * Latch first dead-nexus outcome (P3-05). Idempotent once ended.
   */
  syncOutcome() {
    if (this.outcome.ended) return this.outcome;
    this.outcome = resolveMatchOutcome(this.entities.getStructures());
    return this.outcome;
  }

  isMatchEnded() {
    return this.outcome.ended === true;
  }

  seedSandbox({ player, champions = [], structures = [] } = {}) {
    this.entities.clear();
    if (!player) {
      throw new Error('MatchRuntime.seedSandbox requires player');
    }
    this.entities.add(player);
    this.entities.setPlayer(player);
    for (const champion of champions) {
      this.entities.add(champion);
    }
    for (const structure of structures) {
      this.entities.add(structure);
    }
  }

  toOrchestratorEntityFields() {
    const player = this.entities.getPlayer();
    const secondaryChampions = this.entities
      .getChampions()
      .filter((c) => c !== player);
    return {
      mapId: this.mapId,
      championEntity: player,
      secondaryChampions,
      allTurretEntities: this.entities.getStructures(),
      entityRegistry: this.entities
    };
  }

  getDamageableTargets({ minionDomains = [], jungleMonsters = [] } = {}) {
    const fromRegistry = this.entities.getDamageable();
    const extras = [...minionDomains, ...jungleMonsters].filter(
      (e) => e && !e.isDead
    );
    const seen = new Set(fromRegistry);
    for (const extra of extras) {
      if (!seen.has(extra)) {
        fromRegistry.push(extra);
        seen.add(extra);
      }
    }
    return fromRegistry;
  }
}
